/*
 * Plays the Iconic mark's shine (assets/app.scss): the in-text `.ew-iconic` and the photo badge
 * `.ew-b` under `.ew-m-sweep`. Every 5 s each mark on screen gets `data-shine` for the 2 s play --
 * the star swells and glows, a glint flashes beside it, a light passes over the word -- and loses
 * it again long before the next tick, so the next play restarts without a forced style flush.
 * The greeting eye beside "See the eVoucher" (`.ewg-peek`) rides the same clock: it blinks and
 * its sparkle twinkles; so does a button marked `.ew-sheen` (the light crosses it, `[data-sheen]`
 * in assets/app.scss).
 *
 * It replaces a 5 s CSS loop that never stopped. None of the animated properties runs on the
 * compositor, so the loop cost a restyle, a layout and a repaint on every frame, for every mark,
 * off screen too and through the 3 s of each cycle in which nothing moves: the four marks on a PDP
 * kept the page repainting 60 times a second for as long as it was open.
 *
 * One clock for the page, so the marks on screen shine together. Marks are looked up on every tick
 * (the rail, the cards and the cart's sign are drawn and redrawn by script), so a new one joins
 * within 5 s, and one scrolled into view waits for the next tick, as a mark on the loop did through
 * the still part of its cycle. Nothing plays in a hidden tab, under reduced motion, or on a mark
 * that is laid out but not shown (the cart's sign before its switch is on is `visibility: hidden`).
 *
 * No globals.
 */
(function () {
  const MARKS = '.ew-iconic, .ew-m-sweep .ew-b, .ewg-peek, .ew-sheen';
  const EVERY_MS = 5000;
  // The play is 2 s; the attribute stays a little longer so its last frame is never cut.
  const PLAY_MS = 2200;

  const watched = new Set();
  const onScreen = new Set();
  let observer = null;
  let started = false;

  function still() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function shown(mark) {
    return (
      typeof mark.checkVisibility !== 'function' ||
      mark.checkVisibility({ visibilityProperty: true })
    );
  }

  // Watch every mark on the page, and let go of the ones a redraw has removed.
  function collect() {
    watched.forEach(function (mark) {
      if (mark.isConnected) return;
      observer.unobserve(mark);
      watched.delete(mark);
      onScreen.delete(mark);
    });
    document.querySelectorAll(MARKS).forEach(function (mark) {
      if (watched.has(mark)) return;
      watched.add(mark);
      observer.observe(mark);
    });
  }

  function play() {
    if (document.hidden || still()) return;
    const playing = [];
    onScreen.forEach(function (mark) {
      if (!shown(mark)) return;
      mark.setAttribute('data-shine', '');
      playing.push(mark);
    });
    if (!playing.length) return;
    window.setTimeout(function () {
      playing.forEach(function (mark) {
        mark.removeAttribute('data-shine');
      });
    }, PLAY_MS);
  }

  function init() {
    if (typeof IntersectionObserver !== 'function') return;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      });
      // The first play as soon as a mark is known to be on screen, as the loop played on load.
      if (!started && onScreen.size) {
        started = true;
        play();
      }
    });
    collect();
    window.setInterval(function () {
      collect();
      play();
    }, EVERY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
